import React, { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSubscription, useSelectedAccountId } from '../../hooks/useSubscription'
import { useAuth } from '../../hooks/useAuth'
import { api } from '../../services/api'
import './SubscriptionSuccess.css'
import {
  trackSubscriptionPurchase,
  trackSubscribe,
  hashEmail,
  hashExternalId,
} from '../../utils/analytics'

/**
 * Success page after Stripe Checkout completion
 * Shows confirmation and redirects to dashboard
 *
 * Phase 3: Now revalidates account-level subscription after checkout
 */
const SubscriptionSuccess = () => {
  const { t } = useTranslation('subscription')
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  // Phase 3: Try to get accountId from URL (if backend provides it), fallback to selected account
  const urlAccountId = searchParams.get('account_id')
  const selectedAccountId = useSelectedAccountId()
  const accountId = urlAccountId || selectedAccountId

  const { plan, billingCycle, mutate } = useSubscription(accountId)
  const { user } = useAuth()
  const [hasTracked, setHasTracked] = useState(false)

  const sessionId = searchParams.get('session_id')

  useEffect(() => {
    // Revalidate subscription data after successful checkout
    mutate()
  }, [mutate])

  // Fetch Stripe session details and track purchase
  useEffect(() => {
    const fetchAndTrackPurchase = async () => {
      if (!sessionId || hasTracked) return;

      try {
        // Fetch real Stripe session data
        const sessionData = await api.getCheckoutSession(sessionId);

        if (sessionData) {
          // Extract purchase details from session
          const {
            amount_total,
            currency,
            metadata = {},
            line_items = []
          } = sessionData;

          // Get CAPI event ID from metadata (set during checkout initiation)
          // Note: Browser context was already stored before checkout redirect
          // so the webhook has access to fbp/fbc for CAPI deduplication
          const eventId = metadata.capi_event_id;

          // Find main subscription item
          const mainItem = line_items.find(item =>
            item.description && item.description.toLowerCase().includes('plan')
          );

          // Extract addons (any additional line items)
          const addonItems = line_items.filter(item =>
            item.description && !item.description.toLowerCase().includes('plan')
          ).map(item => ({
            id: item.price?.product || item.description,
            name: item.description,
            price: (item.amount_total || 0) / 100 // Convert from cents
          }));

          // Track with real data (including eventId for CAPI deduplication)
          const planId = metadata.plan_id || plan;
          const billingPeriod = metadata.billing_period || billingCycle?.type;
          const mainPrice = mainItem ? (mainItem.amount_total / 100) : ((amount_total || 0) / 100);

          let hashedEmail = null
          let hashedExternalId = null
          if (user?.email && user?.id) {
            [hashedEmail, hashedExternalId] = await Promise.all([
              hashEmail(user.email),
              hashExternalId(user.id),
            ])
          }

          trackSubscriptionPurchase(
            planId,
            billingPeriod,
            mainPrice,
            {
              transactionId: sessionId,
              eventId,
              hashedEmail,
              hashedExternalId,
              addons: addonItems,
              totalValue: (amount_total || 0) / 100
            }
          );

          // Also track Subscribe event for better Meta optimization
          trackSubscribe(planId, billingPeriod, mainPrice, {
            eventId: eventId ? `${eventId}_subscribe` : undefined,
            hashedEmail,
            hashedExternalId,
          });

          setHasTracked(true);
        }
      } catch (error) {
        console.error('Failed to fetch checkout session details:', error);

        // Fallback: Track with estimated data if API fails
        if (plan && plan !== 'free' && billingCycle?.type && !hasTracked) {
          const estimatedPrice = billingCycle.type === 'yearly'
            ? (plan === 'starter' ? 228 : 588)
            : (plan === 'starter' ? 24 : 59);

          let hashedEmail = null
          let hashedExternalId = null
          if (user?.email && user?.id) {
            [hashedEmail, hashedExternalId] = await Promise.all([
              hashEmail(user.email),
              hashExternalId(user.id),
            ])
          }

          trackSubscriptionPurchase(plan, billingCycle.type, estimatedPrice, {
            transactionId: sessionId,
            hashedEmail,
            hashedExternalId,
          });
          setHasTracked(true);
        }
      }
    };

    fetchAndTrackPurchase();
  }, [sessionId, plan, billingCycle, hasTracked])

  const handleGoToDashboard = () => {
    navigate('/dashboard')
  }

  return (
    <div className="subscription-success-page">
      <div className="success-container">
        {/* Success Icon */}
        <div className="success-icon-wrapper">
          <div className="success-icon">
            <svg
              className="checkmark"
              viewBox="0 0 52 52"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                className="checkmark-circle"
                cx="26"
                cy="26"
                r="25"
                fill="none"
              />
              <path
                className="checkmark-check"
                fill="none"
                d="M14.1 27.2l7.1 7.2 16.7-16.8"
              />
            </svg>
          </div>
        </div>

        {/* Success Message */}
        <div className="success-content">
          <h1 className="success-title">{t('success.title')}</h1>
          <p className="success-message">
            {t('success.message')}
          </p>

          {/* Session ID (for debugging) */}
          {sessionId && (
            <p className="session-id">
              {t('success.sessionId')} <code>{sessionId}</code>
            </p>
          )}

          {/* Benefits List */}
          <div className="benefits-box">
            <h3>{t('success.whatHappensNow.title')}</h3>
            <ul className="benefits-list">
              <li>
                <span className="benefit-icon">✓</span>
                <span>{t('success.whatHappensNow.benefits.planActive')}</span>
              </li>
              <li>
                <span className="benefit-icon">✓</span>
                <span>{t('success.whatHappensNow.benefits.confirmationEmail')}</span>
              </li>
              <li>
                <span className="benefit-icon">✓</span>
                <span>{t('success.whatHappensNow.benefits.useFeatures')}</span>
              </li>
              <li>
                <span className="benefit-icon">✓</span>
                <span>{t('success.whatHappensNow.benefits.autoBilling')}</span>
              </li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="success-actions">
            <button
              className="btn-primary"
              onClick={handleGoToDashboard}
            >
              {t('success.buttons.toDashboard')}
            </button>
            <button
              className="btn-secondary"
              onClick={() => navigate('/subscription', { state: { reloadAddons: true } })}
            >
              {t('success.buttons.manageSubscription')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SubscriptionSuccess
